import * as Yup from 'yup'
import { OperationType, PayoutToken } from '@archax/shared-types'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from '@tanstack/react-query'
import Dialog from '../../../components/Dialog/Dialog'
import TextField from '../../../components/ui/TextField/TextField'
import { tokenAmountValidator } from '../../../util/yup-validators'
import { createOperationForPayoutContract } from '../../../api/operations'
import onApiError from '../../../util/on-api-error'
import { Button, ButtonGroup } from '@mui/material'

type SetPayoutPerIndexProps = {
  payoutContractId: string
  payoutToken: PayoutToken
  onClose: () => void
  onConfirm: () => void
}
export default function SetPayoutPerIndex({ payoutContractId, payoutToken, onClose }: SetPayoutPerIndexProps) {
  const { t } = useTranslation()

  const { mutate: setPayoutPerIndexMutation } = useMutation(
    (data: { payoutPerIndex: number }) =>
      createOperationForPayoutContract(
        OperationType.PayoutContractSetPayoutPerIndex,
        { payoutPerIndex: Number(data.payoutPerIndex) },
        { payoutContractId },
      ),
    {
      onSuccess: () => {
        toast.success(t('payoutContracts.showDetails.actions.setPayoutPerIndex.successMessage'))
      },
      onError: onApiError,
    },
  )

  const validationSchema = Yup.object()
    .shape({
      payoutPerIndex: tokenAmountValidator({ decimals: payoutToken.decimals, positive: true }),
    })
    .required()

  const initialValues = { payoutPerIndex: 0 }
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<{ payoutPerIndex: number }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = (data: { payoutPerIndex: number }) => {
    setPayoutPerIndexMutation(data)
    onClose()
  }

  return (
    <Dialog title={t('payoutContracts.showDetails.actions.setPayoutPerIndex.message')} open={true} onClose={onClose}>
      <form data-testid="set-payout-per-index" onSubmit={handleSubmit(onSubmit)}>
        <TextField
          data-testid="set-payout-per-index__input"
          name="payoutPerIndex"
          type="number"
          control={control}
          label={t('payoutContracts.showDetails.actions.setPayoutPerIndex.input')}
        />

        <ButtonGroup fullWidth>
          <Button
            data-testid="set-payout-per-index__submit-button"
            disabled={!isValid || isSubmitting}
            type="submit"
            variant="contained"
            size="large"
            color="primary"
            fullWidth
          >
            {t('payoutContracts.showDetails.actions.setPayoutPerIndex.button')}
          </Button>
        </ButtonGroup>
      </form>
    </Dialog>
  )
}
